/**
 * Die Verteilregel eines Mieterstrom-Konzepts in Worten (HM-097, ADR 0027).
 *
 * Name und Kurzbeschreibung kommen aus `@hausmeter/i18n`; hier steht nur der
 * Absatz, der einer Wohnung erklärt, **wie ihr Anteil entstanden ist** — er
 * hängt an der Regel und stand bis HM-097 fest im Text der Seite, als es nur
 * eine gab. Ein Text, der die dynamische Verteilung beschreibt, unter einem
 * Wert aus dem Summenzählermodell wäre die stillste Art falscher Auskunft.
 */
import { t } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";

export type PvDistribution = ApiComponents["schemas"]["PvDistribution"];

export const PV_DISTRIBUTIONS: readonly PvDistribution[] = ["dynamic", "equal_fill", "annual_sum"];

export function distributionLabel(distribution: PvDistribution): string {
  return t(`distribution.${distribution}`, "de-DE");
}

export function distributionDescription(distribution: PvDistribution): string {
  return t(`distribution.${distribution}.description`, "de-DE");
}

/** Wie der PV-Anteil dieser Wohnung entsteht — der Absatz unter „Herkunft". */
export function distributionExplanation(distribution: PvDistribution): string {
  switch (distribution) {
    case "dynamic":
      return (
        "Der PV-Anteil einer Wohnung ist keine Messung: Was alle Zähler im Haus zusammen mehr " +
        "zählen als der Summenzähler am Netz, kam aus der Anlage — und jede Viertelstunde " +
        "bekommt jede Wohnung davon den Teil, der ihrem Anteil am Verbrauch des Hauses " +
        "entspricht (dynamische Verteilung). Der Netzanteil ist der Rest ihres Verbrauchs; " +
        "beides zusammen ist, was der Zähler der Wohnung gezählt hat, und die Netzanteile " +
        "aller Wohnungen zusammen sind genau der Netzbezug."
      );
    case "equal_fill":
      return (
        "Der PV-Anteil einer Wohnung ist keine Messung: Was alle Zähler im Haus zusammen mehr " +
        "zählen als der Summenzähler am Netz, kam aus der Anlage — und jede Viertelstunde wird " +
        "es zunächst zu gleichen Teilen auf alle Wohnungen und Verbraucher verteilt. Wer weniger " +
        "verbraucht hat als seinen Teil, bekommt nur seinen Verbrauch; der Rest geht an die, " +
        "deren Bedarf noch offen ist, bis nichts mehr übrig ist. Der Netzanteil ist der Rest des " +
        "Verbrauchs der Wohnung."
      );
    case "annual_sum":
      return (
        "Nach dem Summenzählermodell gibt es keinen PV-Anteil je Viertelstunde: Bekannt ist nur, " +
        "wie viel Solarstrom im Zeitraum im Gebäude geblieben ist, nicht wann und wo. Diese Menge " +
        "wird nach dem Anteil der Wohnung am Verbrauch des Zeitraums aufgeteilt — ein Ladevorgang " +
        "in der Nacht zählt dabei wie Verbrauch am Mittag. Verbindlich ist die Aufteilung über das " +
        "Abrechnungsjahr; für einen kürzeren Zeitraum ist der Wert vorläufig und nach demselben " +
        "Schlüssel gebildet."
      );
  }
}
